import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import ChartHeader from '../ChartHeader';
import ChartEmpty, { CHART_HEIGHT } from '../ChartEmpty';
import { images } from '../../common/images';
import { PHASE_COLORS, phaseName } from '../LineCharts/phaseReadings';


export default function UnitConsumedBarChart({ carbonConsumedData: barDatas }) {
  const [series, setSeries] = useState([]);
  const [options, setOptions] = useState({});

  useEffect(() => {
    // Cleared rather than left as they were, or the last device's bars
    // would stay up for one with no data.
    if (barDatas.length === 0) {
      setSeries([])
      return
    }

    let categories = []
    let data = []
    for (let i of barDatas) {
      
      if (i !== undefined && Object.keys(i).length !== 0 && i.categories.length !== 0) {

        categories = i.categories

        data = barDatas.map((carbonData) => {
          if (carbonData && carbonData.data) {
            return {
              ...carbonData,
              data: carbonData.data.map((value) => value),
            };
          } else {
            return carbonData;
          }
        });
      }
    }
    // Named by phase alone: the chart is already for one device.
    setSeries(data.map((line) => (line ? { ...line, name: phaseName(line.name) } : line)))
    const axisText = { colors: '#8a8f96', fontSize: '11px', fontFamily: 'manrope' }
    setOptions({
      chart: {
        stacked: true,
        fontFamily: 'manrope',
        toolbar: { show: true, tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false } },
        animations: { enabled: false },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 4,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
        },
      },
      dataLabels: {
        enabled: false
      },
      // A thin white edge between stacked phases and neighbouring bars.
      stroke: {
        show: true,
        width: 1,
        colors: ['#ffffff']
      },
      grid: {
        borderColor: '#eef0f3',
        xaxis: { lines: { show: false } },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        fontFamily: 'manrope',
        fontSize: '12px',
        labels: { colors: '#495057' },
        markers: { width: 10, height: 10, radius: 10 },
        itemMargin: { horizontal: 8 },
      },
      xaxis: {
        categories: categories,
        // Every hour has a bar, but only every third is labelled, flat.
        labels: {
          rotate: 0,
          hideOverlappingLabels: true,
          style: axisText,
          formatter: (value) => {
            const index = categories.indexOf(value)
            return categories.length > 12 && index % 3 !== 0 ? '' : value
          },
        },
        axisBorder: { color: '#eef0f3' },
        axisTicks: { show: false },
      },
      yaxis: {
        forceNiceScale: true,
        tickAmount: 4,
        title: {
          text: 'kWh',
          style: { color: '#8a8f96', fontSize: '12px', fontWeight: 500, fontFamily: 'manrope' },
        },
        labels: {
          style: axisText,
          formatter: (val) => (val === null || val === undefined ? '' : Number.isInteger(Number(val)) ? String(val) : Number(val).toFixed(1)),
        },
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        // Every phase of the hour together, with the hour's total.
        shared: true,
        intersect: false,
        y: {
          formatter: (val) => (val === null || val === undefined ? '-' : `${Number(val).toFixed(2)} kWh`)
        }
      }

    })
  }, [barDatas])

  // Bars only when some hour (or day) used something; all zeros or no series
  // at all reads as nothing to show, like the reading charts.
  const hasData = series.some((line) => line?.data?.some((value) => Number(value) > 0))

  return (
    <div>
      <ChartHeader
        name="Units Consumed"
        image={images.unitConsumed}
        textColor="#1AC821"
        iconBackgroundColor="rgba(26, 200, 33, 0.10)"
      />
      {hasData ? (
        <ReactApexChart
          series={series}
          options={{ ...options, colors: PHASE_COLORS }}
          type="bar"
          height={CHART_HEIGHT}
        />
      ) : (
        <ChartEmpty kind="bar" title="No units consumed" />
      )}
    </div>
  );
}
